/**
 * FCM Push Notifications for Capacitor (Android/iOS).
 *
 * Registers the device with FCM, sends the token to the backend,
 * and handles incoming push notifications when the app is in the foreground.
 */

import { isCapacitor } from '../api/config';
import { getEntityUrl } from '../utils/entityUrls';
import { applyNotificationDeepLinkTab } from './deepLinkTab';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let PushNotifications: any = null;
let initialized = false;

/**
 * Resolve the in-app navigation path for a tapped FCM push, from its data payload.
 *
 * The backend (`shared/notifications/service.py::_send_push_notification`) sends a
 * flat string map carrying `entity_type` + `entity_id` (and, for some events,
 * `conversation_id` / `ticket_id`). We deep-link generically through the Entity URL
 * Registry so every notify-worthy entity routes correctly with no per-type allowlist:
 *
 * 1. `conversation_id` → the messaging conversation (query-param route the registry
 *    doesn't model; messaging pushes carry an `entity_type` of `message`, not the
 *    conversation, so this must take precedence).
 * 2. `entity_type` + `entity_id` → `getEntityUrl` (task, deal, ticket, item, …). The
 *    whole `data` map is passed as context so contextual templates (e.g. `board_id`)
 *    fill when present. New-message ticket pushes additionally deep-link to the
 *    Messages tab (the one per-type tweak — see below).
 * 3. `ticket_id` → support ticket (legacy fallback for payloads without `entity_type`).
 *
 * Returns null when nothing resolves — the caller then opens the app at its default
 * route so a tap always brings the app to the foreground.
 */
export function resolvePushNavigationUrl(
  data: Record<string, string>,
): string | null {
  if (data.conversation_id) {
    return `/messaging?conversation=${encodeURIComponent(data.conversation_id)}`;
  }

  if (data.entity_type && data.entity_id) {
    const url = getEntityUrl({
      entityType: data.entity_type,
      entityId: data.entity_id,
      context: data,
    });
    if (url) {
      // Some types deep-link to a specific tab (ticket message → Messages,
      // comment mention → Comments); centralized in applyNotificationDeepLinkTab,
      // kept in sync with notificationHref() (SystemNotificationsBell), the web
      // toast handler (GlobalNotificationHandler) and the backend email CTA. The
      // backend sends the notification type as `data.type`
      // (service.py::_send_push_notification).
      return applyNotificationDeepLinkTab(url, data.type);
    }
  }

  if (data.ticket_id) {
    return `/support/tickets/${encodeURIComponent(data.ticket_id)}`;
  }

  return null;
}

/**
 * Initialize FCM push notifications.
 * Call after the user is authenticated so we can register the token with the backend.
 */
export async function initPushNotifications(
  onTokenReceived: (token: string) => void,
  onNotificationReceived?: (data: Record<string, string>) => void,
  onNotificationTapped?: (data: Record<string, string>) => void,
): Promise<void> {
  if (!isCapacitor || initialized) return;

  try {
    const mod = await import('@capacitor/push-notifications');
    PushNotifications = mod.PushNotifications;
  } catch {
    return;
  }

  // Request permission
  const permResult = await PushNotifications.requestPermissions();
  if (permResult.receive !== 'granted') {
    return;
  }

  // Listen for registration success
  await PushNotifications.addListener(
    'registration',
    (token: { value: string }) => {
      onTokenReceived(token.value);
    },
  );

  // Listen for registration errors
  await PushNotifications.addListener(
    'registrationError',
    (error: { error: string }) => {
      console.error('Push registration failed:', error.error);
    },
  );

  // Listen for push notifications received while app is in foreground
  if (onNotificationReceived) {
    await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: { data?: Record<string, string> }) => {
        onNotificationReceived(notification.data ?? {});
      },
    );
  }

  // Listen for push notification taps (app was in background)
  if (onNotificationTapped) {
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: { notification: { data?: Record<string, string> } }) => {
        onNotificationTapped(action.notification.data ?? {});
      },
    );
  }

  // Register with FCM
  await PushNotifications.register();
  initialized = true;
}

/**
 * Unregister push notifications (e.g., on logout).
 */
export async function unregisterPushNotifications(): Promise<void> {
  if (!PushNotifications) return;

  try {
    await PushNotifications.removeAllListeners();
    initialized = false;
  } catch {
    // Non-critical
  }
}
